import { useParams } from "react-router-dom";
import { Presentation } from "lucide-react";
import { PDFViewer } from "@/components/viewers/PDFViewer";
import {
  GenericError,
  PaywallCard,
  ViewerScaffold,
} from "@/components/viewers/ViewerScaffold";
import { useViewerAccess } from "@/components/viewers/ChapterAccessGate";
import { useSignedUrl } from "@/hooks/useSignedUrl";
import { useViewerTracker } from "@/hooks/useViewerTracker";

export function SlidesPage() {
  const { chapterId } = useParams<{ chapterId: string }>();
  const { access, paywall, shouldFetch } = useViewerAccess(chapterId, "Slide Deck");
  const { reportEndReached } = useViewerTracker({
    chapterId,
    mode: "slides",
    active: paywall === null,
    minDwellSeconds: 90,
  });

  // Completion = last page reached AND the dwell floor met. Flicking the
  // scrollbar to the bottom is not finishing a chapter.
  const handlePageChange = (page: number, totalPages: number) => {
    if (totalPages > 0 && page >= totalPages) reportEndReached();
  };

  const { url, isLoading, isError, isLocked, refresh } = useSignedUrl({
    chapterId,
    assetType: "slides",
    enabled: shouldFetch,
  });

  const chapter = access.chapter;
  const classId = access.needsClassId ?? chapter?.class_id;

  return (
    <ViewerScaffold
      chapterTitle={chapter?.chapter_name ?? null}
      subjectName={null}
      modeLabel="Slide Deck"
      modeIcon={Presentation}
      modeAccent="bg-indigo-500/10 text-indigo-600 dark:text-indigo-400"
    >
      {paywall ?? (
        isLocked ? (
          <PaywallCard modeLabel="Slide deck" classId={classId} />
        ) : isError ? (
          <GenericError onRetry={refresh} />
        ) : url ? (
          <PDFViewer url={url} viewMode="slide" onPageChange={handlePageChange} />
        ) : isLoading ? (
          <div className="flex-1 grid place-items-center text-sm text-text-muted">
            Preparing the slides…
          </div>
        ) : null
      )}
    </ViewerScaffold>
  );
}
